/**
 * Component based in the Flex component of AntD v5
 * https://github.com/ant-design/ant-design/blob/master/components/flex/index.tsx
 */

import { forwardRef, useMemo } from 'react'

interface FlexProps extends React.HTMLAttributes<HTMLElement> {
  vertical?: boolean
  wrap?: boolean | React.CSSProperties['flexWrap']
  justify?: React.CSSProperties['justifyContent']
  align?: React.CSSProperties['alignItems']
  flex?: React.CSSProperties['flex']
  gap?: React.CSSProperties['gap']
  component?: React.ElementType
}

export const Flex = forwardRef<HTMLElement, FlexProps>(function Flex(
  {
    vertical,
    wrap,
    justify,
    align,
    flex,
    gap,
    style,
    component: Component = 'div',
    children,
    ...props
  },
  ref,
) {
  const styles = useMemo(() => {
    const newStyle: React.CSSProperties = {
      display: 'flex',
      flexDirection: vertical ? 'column' : 'row',
    }

    if (wrap) newStyle.flexWrap = typeof wrap === 'boolean' ? 'wrap' : wrap
    if (justify) newStyle.justifyContent = justify
    if (align) newStyle.alignItems = align
    if (flex) newStyle.flex = flex
    if (gap) newStyle.gap = gap

    return { ...newStyle, ...style }
  }, [vertical, wrap, justify, align, flex, gap, style])

  return (
    <Component ref={ref} style={styles} {...props}>
      {children}
    </Component>
  )
})
